import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NAV_SECTIONS } from '../config/navConfig';
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat';
import chartStoryLogo from '../asset/Chart Story 72.png';
import AppRibbon from './AppRibbon';
import './AppLayout.css';

/**
 * AppLayout — unified header + sidebar wrapper for every protected page.
 *
 * The hamburger button lives INSIDE the header as a flex item, so it
 * can never overlap the logo regardless of page-level CSS.
 *
 * Usage:
 *   <AppLayout pageBadge="Daily Monitor">
 *     <div className="my-page-root">...</div>
 *   </AppLayout>
 */
const AppLayout = ({ pageBadge = '', children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logoutAction } = useAuth();

  // Real-time presence heartbeat — runs on every authenticated page.
  usePresenceHeartbeat();

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';
  const isOnAdminRoute = location.pathname.startsWith('/admin');
  const adminSection = NAV_SECTIONS.find((s) => s.category === 'Admin');
  const showAdminSubnav = isAdmin && isOnAdminRoute && !!adminSection;
  const subnavRef = useRef(null);
  const [subnavHeight, setSubnavHeight] = useState(0);

  useEffect(() => {
    if (!showAdminSubnav) { setSubnavHeight(0); return; }
    const el = subnavRef.current;
    if (!el) return;
    const update = () => setSubnavHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [showAdminSubnav]);

  useEffect(() => {
    if (isOnAdminRoute) setAdminExpanded(true);
  }, [isOnAdminRoute]);
  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const affirmationText = user?.affirmation?.enabled && user?.affirmation?.text?.trim()
    ? user.affirmation.text.trim()
    : '';

  return (
    <>
      {/* ── Desktop icon rail (hidden <=1024px via CSS) ────────── */}
      <AppRibbon />

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="app-header">
        {/* Hamburger inside the flex row — never floats over content */}
        <button
          className="app-header__menu-btn"
          onClick={() => setSidebarOpen((p) => !p)}
          aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
        >
          ☰
        </button>

        {/* Logo — left aligned with brand text */}
        <div className="app-header__logo">
          <img src={chartStoryLogo} alt="CS Analytics" className="logo-img" />
          <div className="app-header__brand">
            <div className="app-header__brand-title">CS Insights Pro</div>
            <div className="app-header__brand-sub">Data Analytics Platform</div>
          </div>
        </div>

        {/* Right: badge + profile */}
        <div className="app-header__right">
          {affirmationText && (
            <div className="header-affirmation" aria-label="Personal affirmation">
              <div className="header-affirmation__track">
                <span className="header-affirmation__text">{affirmationText}</span>
                <span className="header-affirmation__text" aria-hidden="true">{affirmationText}</span>
              </div>
            </div>
          )}
          {pageBadge && (
            <div className="page-badge">
              <span className="live-dot">{pageBadge}</span>
            </div>
          )}
          <button
            className="profile-avatar-btn"
            onClick={() => navigate('/profile')}
            title="My Profile"
            aria-label="Go to profile"
          >
            {initials}
          </button>
        </div>
      </header>

      {/* ── Admin Sub-Navbar (only on /admin/* for admins) ───── */}
      {showAdminSubnav && (
        <nav className="cs-admin-subnav" aria-label="Admin navigation" ref={subnavRef}>
          <div className="cs-admin-subnav__inner">
            {adminSection.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`cs-admin-subnav__link${isActive(item.path) ? ' active' : ''}`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <span className="cs-admin-subnav__icon">{Icon ? <Icon /> : null}</span>
                  <span className="cs-admin-subnav__label">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      {/* ── Sidebar Overlay ────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="cs-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar Panel ──────────────────────────────────────── */}
      <aside
        className={`cs-sidebar${sidebarOpen ? ' open' : ''}`}
        aria-label="Site navigation"
      >
        <div className="cs-sidebar__head">
          <div className="cs-sidebar__brand-icon">
            <img src={chartStoryLogo} alt="CS Analytics" className="cs-sidebar__brand-logo" />
          </div>
          <div className="cs-sidebar__brand-text">
            <div className="cs-sidebar__brand-title">CS Insights Pro</div>
            <div className="cs-sidebar__brand-sub">Data Analytics Platform</div>
          </div>
          <button
            className="cs-sidebar__close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
          >
            ✕
          </button>
        </div>

        <nav className="cs-sidebar__nav">
          {NAV_SECTIONS.filter(s => !s.adminOnly || isAdmin).map((section, si) => {
            const isAdminSection = section.category === 'Admin';
            const showItems = !isAdminSection || adminExpanded;
            return (
              <div key={section.category} className="cs-nav-section">
                {si > 0 && <div className="cs-nav-section-divider" />}
                {isAdminSection ? (
                  <button
                    type="button"
                    className="cs-nav-section-label cs-nav-section-label--collapsible"
                    onClick={() => setAdminExpanded(prev => !prev)}
                    aria-expanded={adminExpanded}
                    aria-controls="admin-nav-items"
                  >
                    <span>{section.category}</span>
                    <span className="cs-nav-section-label__arrow" aria-hidden="true">
                      {adminExpanded ? '▾' : '▸'}
                    </span>
                  </button>
                ) : (
                  <div className="cs-nav-section-label">{section.category}</div>
                )}
                {showItems && (
                  <div id={isAdminSection ? 'admin-nav-items' : undefined}>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`cs-nav-link${isActive(item.path) ? ' active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                          aria-current={isActive(item.path) ? 'page' : undefined}
                        >
                          <span className="cs-nav-link__icon">{Icon ? <Icon /> : null}</span>
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="cs-sidebar__footer">
          <div className="cs-sidebar__footer-actions">
            <button
              type="button"
              className="cs-sidebar__footer-btn"
              onClick={() => {
                setSidebarOpen(false);
                navigate('/profile');
              }}
              title="My Profile"
              aria-label="My Profile"
            >
              <span className="cs-sidebar__footer-btn-icon">👤</span>
              <span>Profile</span>
            </button>
            <button
              type="button"
              className="cs-sidebar__footer-btn cs-sidebar__footer-btn--danger"
              onClick={async () => {
                setSidebarOpen(false);
                try { await logoutAction(); } catch (e) { /* ignore */ }
                navigate('/login');
              }}
              title="Logout"
              aria-label="Logout"
            >
              <span className="cs-sidebar__footer-btn-icon">↪</span>
              <span>Logout</span>
            </button>
          </div>
          <div className="cs-sidebar__footer-copy">ChartStory · CS Insights Pro © {new Date().getFullYear()}</div>
        </div>
      </aside>

      {/* ── Page content ───────────────────────────────────────── */}
      {showAdminSubnav ? (
        <div
          className="cs-content-shell cs-admin-shell"
          style={{ paddingTop: `calc(var(--layout-header-height, 52px) + ${subnavHeight}px)` }}
        >
          {children}
        </div>
      ) : (
        <div className="cs-content-shell">{children}</div>
      )}
    </>
  );
};

export default AppLayout;
